"use client";

import React from "react";
import { CheckCircle2, Leaf, Loader2 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function JoinTheWellnessCircle() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="83662" id="newsletter-cta" className="dark bg-[#1A1A2E] text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 opacity-10 mix-blend-overlay pointer-events-none">
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/mortar_pestle_dark.png')] bg-cover bg-center bg-no-repeat transform scale-105 transition-transform duration-1000" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A2E]/90 via-[#1A1A2E]/70 to-[#1A1A2E]/90" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 relative z-10 max-w-4xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-[#3B8FD4]/10 rounded-full mb-6">
            <Leaf className="size-8 text-[#3B8FD4]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-4 text-white">
            Prioritize Your Spinal Health
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Join the Bodywerks Community to receive expert chiropractic insights, recovery protocols, and exclusive health updates delivered straight to your inbox.
          </p>
        </div>

        <div className="bg-[#1A1A2E]/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-10 shadow-2xl shadow-black/40">
          <FormProvider formKey="newsletter_signup_bodywerksbloglistingpage" formId="newsletter_signup_bodywerksbloglistingpage" sectionName="Newsletter CTA" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          interests: [],
          agree: false
        }} validationRules={{
          full_name: {
            required: "Full name is required",
            minLength: 2
          },
          email: {
            required: "Email address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          },
          agree: {
            required: "You must agree to receive updates"
          }
        }} submitText="Subscribe" successMessage="Welcome to the Bodywerks Community! You're now on the path to better alignment and health.">
            <WellnessForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function WellnessForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-[#3B8FD4]/20 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="size-8 text-[#3B8FD4]" />
        </div>
        <h3 className="text-2xl font-serif font-bold mb-2 text-white">Stay Aligned!</h3>
        <p className="text-slate-300 max-w-md">{successMessage}</p>
      </div>;
  }
  const interestOptions = ["Back Pain Relief", "Sports Performance", "Posture Improvement", "Clinic Updates"];
  const inputClasses = "flex h-12 w-full rounded-none border-t-0 border-l-0 border-r-0 border-b-2 border-[#E2E8F0] bg-transparent px-0 py-2 text-base ring-0 placeholder:text-slate-500/70 focus-visible:outline-none focus-visible:border-[#3B8FD4] disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 font-sans";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-sans">Full Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="Jane Doe" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/90 font-sans">Email Address</FormLabel>
              <FormControl>
                <input type="email" placeholder="jane@example.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/90 font-sans">Phone Number</FormLabel>
            <FormControl>
              <input type="tel" placeholder="+357 99 000000" className={inputClasses} {...field} />
            </FormControl>
            <FormDescription className="text-slate-400/70 font-sans">
              Optional, for appointment reminders and SMS updates
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="interests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/90 text-base font-sans">Health Goals</FormLabel>
            <FormDescription className="text-slate-400/70 mb-4 font-sans">
              Select the areas that matter most to you
            </FormDescription>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              {interestOptions.map((option, i) => <div key={i} data-index={i} className="flex items-center space-x-3">
                  <input type="checkbox" id={`interest-${i}`} className="size-5 rounded border-[#E2E8F0] bg-transparent text-[#3B8FD4] focus:ring-[#3B8FD4] focus:ring-offset-[#1A1A2E] transition-colors cursor-pointer" checked={field.value?.includes(option)} onChange={e => {
            const current = field.value || [];
            const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
            field.onChange(updated);
          }} />
                  <label htmlFor={`interest-${i}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer text-slate-300 hover:text-white transition-colors font-sans">
                    {option}
                  </label>
                </div>)}
            </div>
            <FormMessage />
          </FormItem>} />

      <div className="pt-4 border-t border-white/10">
        <FormField control={control} name="agree" render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 bg-white/5 rounded-lg border border-white/5">
              <FormControl>
                <input type="checkbox" className="mt-0.5 size-5 rounded border-[#E2E8F0] bg-transparent text-[#3B8FD4] focus:ring-[#3B8FD4] focus:ring-offset-[#1A1A2E] transition-colors cursor-pointer" checked={field.value} onChange={field.onChange} />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-medium cursor-pointer text-white/90 font-sans">
                  I agree to receive updates
                </FormLabel>
                <FormDescription className="text-xs text-slate-400/70 font-sans">
                  By checking this box, you consent to receive health insights and marketing communications from Bodywerks Clinic.
                </FormDescription>
                <FormMessage />
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] h-14 text-base font-semibold rounded-full bg-[#3B8FD4] hover:bg-[#3B8FD4]/90 text-white shadow-lg shadow-[#3B8FD4]/20 transition-all duration-300 font-sans">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Subscribing...
          </> : "Subscribe"}
      </Button>
    </Form>;
}